import { useState, FormEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { shortenUrl, ShortenResponse } from '../lib/api'
import styles from './ShortenForm.module.css'

interface Props {
  onCreated: (r: ShortenResponse) => void
}

export default function ShortenForm({ onCreated }: Props) {
  const [url, setUrl] = useState('')
  const [alias, setAlias] = useState('')
  const [expires, setExpires] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [advanced, setAdvanced] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!url.trim()) return
    setLoading(true)
    setError(null)
    try {
      const result = await shortenUrl({
        originalUrl: url.trim(),
        alias: alias.trim() || undefined,
        expiresAt: expires || undefined,
      })
      onCreated(result)
      setUrl('')
      setAlias('')
      setExpires('')
      setAdvanced(false)
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Something went wrong.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <motion.form
      className={styles.form}
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div className={styles.mainRow}>
        <input
          className={styles.urlInput}
          type="url"
          placeholder="https://your-very-long-url.com/goes/here"
          value={url}
          onChange={e => setUrl(e.target.value)}
          required
          autoFocus
        />
        <button className={styles.submitBtn} type="submit" disabled={loading}>
          {loading ? <span className={styles.spinner} /> : 'Shorten →'}
        </button>
      </div>

      <button
        type="button"
        className={styles.advancedToggle}
        onClick={() => setAdvanced(v => !v)}
      >
        {advanced ? '− hide options' : '+ custom alias & expiry'}
      </button>

      <AnimatePresence>
        {advanced && (
          <motion.div
            className={styles.advanced}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className={styles.advancedFields}>
              <div className={styles.field}>
                <label>Custom alias</label>
                <input
                  placeholder="my-link"
                  value={alias}
                  onChange={e => setAlias(e.target.value)}
                  maxLength={50}
                />
              </div>
              <div className={styles.field}>
                <label>Expires at</label>
                <input
                  type="datetime-local"
                  value={expires}
                  onChange={e => setExpires(e.target.value)}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
          <motion.p
            className={styles.error}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            ⚠ {error}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.form>
  )
}
